import { type ReactElement, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { type TvPanelEventView, type TvPanelPairingView, type TvPanelView, type TvPanelsView, type TvRevokeReason, type TvScopeView, type TvViewCode } from '@vello/api-client';
import {
  AUTO_REFRESH_MS,
  Badge,
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  EmptyState,
  Field,
  FleetCentralPresenceDot,
  ListTable,
  Notice,
  PageHeader,
  Panel,
  Row,
  SectionTitle,
  Select,
  Split,
  Stack,
  Switch,
  Text,
  TextInput,
  useAutoRefresh,
  type Column,
  type TimelineEntry,
} from '@vello/web-theme';
import {
  TV_NAME_MAX,
  TV_REVOKE_REASON_CHOICES,
  TV_VIEW_CHOICES,
  mayChangeTvPanel,
  mayDeleteTvPanel,
  newTvDraft,
  sortTvEvents,
  sortTvPanels,
  tvDefaultScope,
  tvDraftChanged,
  tvDraftOf,
  tvDeleteBodyKey,
  tvDraftValid,
  tvInputOf,
  tvDevicePaired,
  tvHomeMarket,
  tvMayRotate,
  tvOperatorName,
  tvPanelAuthority,
  tvPanelStatus,
  tvScopeChoices,
  tvScopeFromValue,
  tvScopeValue,
  tvStatusTone,
  tvViewAllowedInScope,
  withTvScope,
  withTvViewToggled,
  type TvPanelDraft,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { DrawerStack } from '../components/drawer-stack';
import { HistoryTimeline } from '../components/history-timeline';
import { tvHost } from '../tv-url';

/**
 * Operações › Painéis de TV (TV-PANEL-LINKS, D647, ADR-0123): the wall screens of the operations room. An operator registers a panel - a name, the
 * views it shows (one, or several that rotate) and its scope (all cities, one country or one city) - and receives the D561 PAIRING CODE once; the
 * person at the TV opens the TV address and types it, as with any paired device. The list tells each panel's connection from its device's heartbeat;
 * "Gerenciar" opens the panel in the right-hand drawer to change it, issue a new code (while the TV waits) or pair it again (the TV on the wall
 * stops), or revoke it with a reason. Every step is in the panel's history. The code is single use and is not kept, so it cannot be shown again. Layout and type come from the web-theme vocabulary. Neutral: no country, market name or
 * language is named here.
 */

type Notice_ = { readonly tone: 'success' | 'danger'; readonly text: string };

/** The pairing code of a panel the operator has not yet passed on: it is shown this once, then forgotten. */
interface Issued {
  readonly panelId: string;
  readonly name: string;
  readonly code: string;
  readonly expiresAt: string;
}
const issuedOf = (r: TvPanelPairingView): Issued => ({ panelId: r.panel.panelId, name: r.panel.name, code: r.code, expiresAt: r.expiresAt });

/** The viewer's own clock: hh:mm for today, the day too for an older instant. */
function useInstant(): { readonly clock: (iso: string | undefined) => string; readonly full: (iso: string | undefined) => string } {
  const { lang } = useT();
  return useMemo(() => {
    const dayOf = (d: Date): string => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const time = new Intl.DateTimeFormat(lang, { hour: '2-digit', minute: '2-digit' });
    const dayTime = new Intl.DateTimeFormat(lang, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    const full = new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'short' });
    const parse = (iso: string | undefined): Date | undefined => {
      if (iso === undefined) return undefined;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? undefined : d;
    };
    return {
      clock: (iso) => {
        const d = parse(iso);
        if (d === undefined) return '—';
        return dayOf(d) === dayOf(new Date()) ? time.format(d) : dayTime.format(d);
      },
      full: (iso) => {
        const d = parse(iso);
        return d === undefined ? '—' : full.format(d);
      },
    };
  }, [lang]);
}

export function TvPanelsScreen(): ReactElement {
  const { runtime, scope, grants, markets } = useAdmin();
  const { t } = useT();
  const { v, explain, fail, reason } = useVocab();
  const place = usePlaceNames();
  const { confirm } = useConsole();
  const { clock, full } = useInstant();
  const authority = tvPanelAuthority(grants);
  const host = tvHost();

  const [data, setData] = useState<TvPanelsView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [notice, setNotice] = useState<Notice_ | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<Issued | undefined>(undefined);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string | undefined; readonly params?: Readonly<Record<string, string | number>> | undefined }): string =>
      explain(e) ?? t('lmap.err.generic', { code: fail(e) }),
    [explain, fail, t],
  );

  const options = data?.rotateSecondsOptions ?? [];
  const marketNames = data?.markets ?? {};
  const regionNames = data?.regions ?? {};
  const operators = data?.operators ?? {};

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const res = await runtime.ops.tvPanels();
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setData(res.value);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no panels".
      setError(refuse(res.error));
    }
  }, [runtime, refuse]);

  useEffect(() => {
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue });

  // ---- scope words ------------------------------------------------------------------------------------------------------------------------

  const marketName = useCallback((id: string): string => marketNames[id] ?? place.market(id), [marketNames, place]);
  const regionName = useCallback((code: string): string => regionNames[code.toUpperCase()] ?? regionNames[code] ?? place.region(code), [regionNames, place]);
  /** The scope as a list cell says it: the platform, a country's, a state's or a city's name. */
  const scopeName = useCallback(
    (s: TvScopeView): string => (s.kind === 'PLATFORM' ? t('tvpanels.scope.all') : s.kind === 'COUNTRY' ? place.country(s.country) : s.kind === 'REGION' ? regionName(s.region) : marketName(s.market)),
    [t, place, regionName, marketName],
  );
  /** The scope as the select says it: a country or a state carries "todas as cidades" so it is not mistaken for one city. */
  const scopeOption = useCallback(
    (s: TvScopeView): string =>
      s.kind === 'COUNTRY' ? t('tvpanels.scope.country', { country: place.country(s.country) }) : s.kind === 'REGION' ? t('tvpanels.scope.region', { region: regionName(s.region) }) : scopeName(s),
    [t, place, regionName, scopeName],
  );

  const choices = useMemo(() => tvScopeChoices(scope.allowed), [scope.allowed]);
  const startScope = useMemo(() => tvDefaultScope(scope.current, choices), [scope, choices]);

  // ---- the new panel ----------------------------------------------------------------------------------------------------------------------

  const [draft, setDraft] = useState<TvPanelDraft | undefined>(undefined);
  const form = draft ?? newTvDraft(startScope, options);

  const create = async (): Promise<void> => {
    const input = tvInputOf(form, tvHomeMarket(scope.current, tvScopeFromValue(form.scope), markets));
    if (input === undefined) return;
    setBusy(true);
    setNotice(undefined);
    const res = await runtime.ops.createTvPanel(input);
    setBusy(false);
    if (res.ok) {
      setIssued(issuedOf(res.value));
      setDraft(undefined);
      setNotice({ tone: 'success', text: t('tvpanels.done.created') });
    } else setNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const startNew = (): void => {
    setIssued(undefined);
    setNotice(undefined);
    setDraft(undefined);
    window.setTimeout(() => document.getElementById('tvp-new-name')?.focus(), 0);
  };

  // ---- the drawer -------------------------------------------------------------------------------------------------------------------------

  const [openId, setOpenId] = useState<string | undefined>(undefined);
  const [edit, setEdit] = useState<TvPanelDraft | undefined>(undefined);
  const [events, setEvents] = useState<{ readonly rows: readonly TvPanelEventView[]; readonly operators: Readonly<Record<string, string>> } | undefined>(undefined);
  const [eventsError, setEventsError] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [drawerNotice, setDrawerNotice] = useState<Notice_ | undefined>(undefined);

  const panels = useMemo(() => sortTvPanels(data?.panels ?? []), [data]);
  const open = openId === undefined ? undefined : panels.find((p) => p.panelId === openId);

  const readEvents = useCallback(
    async (panelId: string): Promise<void> => {
      setEventsError(false);
      const res = await runtime.ops.tvPanelEvents(panelId);
      if (res.ok) setEvents({ rows: sortTvEvents(res.value.events), operators: res.value.operators });
      else {
        setEvents({ rows: [], operators: {} });
        setEventsError(true);
      }
    },
    [runtime],
  );

  const openPanel = (panel: TvPanelView): void => {
    setOpenId(panel.panelId);
    setEdit(tvDraftOf(panel));
    setEvents(undefined);
    setRevoking(false);
    setRevokeReason('');
    setDrawerNotice(undefined);
    setNotice(undefined);
    void readEvents(panel.panelId);
  };

  const closeDrawer = (): void => {
    setOpenId(undefined);
    setEdit(undefined);
    setEvents(undefined);
    setRevoking(false);
    setRevokeReason('');
    setDrawerNotice(undefined);
    setIssued((i) => (i !== undefined && i.panelId === openId ? undefined : i)); // a link not copied is forgotten
  };

  const save = async (panel: TvPanelView): Promise<void> => {
    const input = edit === undefined ? undefined : tvInputOf(edit);
    if (input === undefined) return;
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.ops.updateTvPanel(panel.panelId, input);
    setBusy(false);
    if (res.ok) {
      setEdit(tvDraftOf(res.value.panel));
      setDrawerNotice({ tone: 'success', text: t('tvpanels.done.updated') });
      await readEvents(panel.panelId);
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  /** "Gerar novo código" while the TV waits (the same device, a new code); "Parear de novo" once paired (a new device: the TV on the wall stops). */
  const pairAgain = async (panel: TvPanelView): Promise<void> => {
    const paired = tvDevicePaired(panel);
    if (paired) {
      const answer = await confirm({
        title: t('tvpanels.repair.title', { name: panel.name }),
        body: t('tvpanels.repair.body'),
        confirmLabel: t('tvpanels.repair.confirm'),
        cancelLabel: t('ui.cancel'),
        tone: 'danger',
        icon: 'rotateCcw',
      });
      if (!answer.confirmed) return;
    }
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.ops.issueTvPanelPairingCode(panel.panelId);
    setBusy(false);
    if (res.ok) {
      setIssued(issuedOf(res.value));
      setDrawerNotice({ tone: 'success', text: t(paired ? 'tvpanels.done.repaired' : 'tvpanels.done.recoded') });
      await readEvents(panel.panelId);
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  const revoke = async (panel: TvPanelView): Promise<void> => {
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.ops.revokeTvPanel(panel.panelId, revokeReason as TvRevokeReason);
    setBusy(false);
    if (res.ok) {
      setRevoking(false);
      setRevokeReason('');
      setIssued((i) => (i !== undefined && i.panelId === panel.panelId ? undefined : i));
      setDrawerNotice({ tone: 'success', text: t('tvpanels.done.revoked') });
      await readEvents(panel.panelId);
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  /** "Excluir painel": a terminal soft delete behind a danger confirmation. An active panel is revoked first by the server (its TV stops at once); the history stays on record. */
  const remove = async (panel: TvPanelView): Promise<void> => {
    const answer = await confirm({
      title: t('tvpanels.delete.confirmTitle', { name: panel.name }),
      body: t(tvDeleteBodyKey(panel)),
      confirmLabel: t('tvpanels.delete.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
    });
    if (!answer.confirmed) return;
    setBusy(true);
    setDrawerNotice(undefined);
    const res = await runtime.ops.deleteTvPanel(panel.panelId);
    setBusy(false);
    if (res.ok) {
      closeDrawer();
      setNotice({ tone: 'success', text: t('tvpanels.done.deleted') });
    } else setDrawerNotice({ tone: 'danger', text: refuse(res.error) });
    await load();
  };

  // ---- the list ---------------------------------------------------------------------------------------------------------------------------

  const viewCell = (p: TvPanelView): ReactElement => {
    const names = p.views.map((c) => v('tv.view', c));
    return (
      <Stack gap={1}>
        <Text>{p.views.length > 1 && p.rotate ? t('tvpanels.rotation', { count: p.views.length }) : names.join(' · ')}</Text>
        {p.views.length > 1 && p.rotate && <Text role="caption" tone="secondary">{names.join(' · ')}</Text>}
      </Stack>
    );
  };

  /** The status in words: the connection with the hour of the last read, or "Revogado" / "Nunca conectada". */
  const statusText = (p: TvPanelView): string => {
    const status = tvPanelStatus(p);
    if (status === 'REVOKED') return v('tv.panel_state', 'REVOKED');
    if (status === 'NEVER') return v('tv.connection', 'NEVER');
    return t('tvpanels.status.at', { status: v('tv.connection', status), time: clock(p.lastSeenAt) });
  };

  const statusCell = (p: TvPanelView): ReactElement => {
    const status = tvPanelStatus(p);
    if (status === 'CONNECTED' || status === 'NO_SIGNAL') {
      return (
        <Row gap={2} align="center">
          <FleetCentralPresenceDot presence={status === 'CONNECTED' ? 'online' : 'away'} />
          <Text role="label" tone={status === 'CONNECTED' ? 'success' : 'warning'} className="tnum">{statusText(p)}</Text>
        </Row>
      );
    }
    return <Badge tone={tvStatusTone(status)}>{statusText(p)}</Badge>;
  };

  const columns: readonly Column<TvPanelView>[] = [
    { key: 'panel', header: t('tvpanels.col.panel'), width: 'minmax(180px, 1.4fr)', render: (p) => <Text as="strong">{p.name}</Text> },
    { key: 'view', header: t('tvpanels.col.view'), width: 'minmax(140px, 1fr)', render: viewCell },
    { key: 'scope', header: t('tvpanels.col.scope'), width: 'minmax(140px, 1fr)', render: (p) => <Text>{scopeName(p.scope)}</Text> },
    { key: 'status', header: t('tvpanels.col.status'), width: 'minmax(170px, 0.9fr)', render: statusCell },
    { key: 'action', header: t('tvpanels.col.action'), width: 'minmax(96px, 0.5fr)', render: (p) => <Text role="label" className="vello-link" aria-label={t('tvpanels.manageOf', { name: p.name })}>{t('tvpanels.manage')}</Text> },
  ];

  /** The panel's history as a timeline (newest first): a revocation is red. `undefined` while it loads. */
  const historyEntries = (): readonly TimelineEntry[] | undefined =>
    events?.rows.map((e) => ({
      id: e.eventId,
      tone: e.type === 'revoked' ? 'danger' : 'neutral',
      title: v('tv.event_type', e.type),
      ...(e.reasonCode === undefined ? {} : { detail: (TV_REVOKE_REASON_CHOICES as readonly string[]).includes(e.reasonCode) ? v('tv.revoke_reason', e.reasonCode) : reason(e.reasonCode) }),
      meta: `${full(e.occurredAt)} · ${tvOperatorName(events?.operators ?? operators, e.actor)}`,
    }));

  // ---- render -----------------------------------------------------------------------------------------------------------------------------

  const pageIssued = issued !== undefined && issued.panelId !== openId ? issued : undefined;
  const drawerIssued = issued !== undefined && issued.panelId === openId ? issued : undefined;

  return (
    <Stack gap={5}>
      <PageHeader
        title={t('tvpanels.title')}
        subtitle={t('tvpanels.subtitle')}
        {...(authority.mayManage ? { actions: <Button variant="secondary" onClick={startNew}>{t('tvpanels.new')}</Button> } : {})}
      />

      {!authority.mayManage && <Notice tone="neutral">{t('tvpanels.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}

      <Split aside="lg" side="end" gap={5}>
        <Stack gap={3}>
          <ListTable
            columns={columns}
            rows={panels}
            rowKey={(p) => p.panelId}
            onRowClick={openPanel}
            {...(open !== undefined ? { selectedKey: open.panelId } : {})}
            loading={data === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('tvpanels.empty.failed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('tvpanels.empty.title')} detail={t('tvpanels.empty.detail')} />}
            minWidth={820}
          />
          <Text role="caption" tone="secondary" as="p">{t('tvpanels.foot')}</Text>
        </Stack>

        {authority.mayManage && (
          <Panel>
            {pageIssued !== undefined ? (
              <PairingBox issued={pageIssued} host={host} expiresText={full(pageIssued.expiresAt)} onDone={() => setIssued(undefined)} />
            ) : (
              <Stack gap={4}>
                <SectionTitle>{t('tvpanels.form.title')}</SectionTitle>
                <PanelForm
                  idPrefix="tvp-new"
                  draft={form}
                  onChange={setDraft}
                  choices={choices}
                  scopeOption={scopeOption}
                  options={options}
                  disabled={busy}
                />
                <Button variant="primary" block disabled={busy || !tvDraftValid(form)} onClick={() => void create()}>{t('tvpanels.form.submit')}</Button>
                <Text role="caption" tone="secondary" as="p">{t('tvpanels.form.note')}</Text>
              </Stack>
            )}
          </Panel>
        )}
      </Split>

      <Drawer open={open !== undefined} onClose={closeDrawer} labelledBy="tvp-drawer-title">
        {open !== undefined && edit !== undefined && (
          <>
            <DrawerHeader
              titleId="tvp-drawer-title"
              title={t('tvpanels.drawer.title', { name: open.name })}
              subtitle={<Badge tone={tvStatusTone(tvPanelStatus(open))}>{statusText(open)}</Badge>}
              onClose={closeDrawer}
              closeLabel={t('ui.close')}
            />
            <DrawerBody>
              <DrawerStack>
                {drawerNotice !== undefined && <Notice tone={drawerNotice.tone}>{drawerNotice.text}</Notice>}
                {drawerIssued !== undefined && <PairingBox issued={drawerIssued} host={host} expiresText={full(drawerIssued.expiresAt)} onDone={() => setIssued(undefined)} />}

                <Stack gap={1}>
                  {!tvDevicePaired(open) && open.state === 'ACTIVE' ? (
                    <Text role="caption" tone="secondary">{t('tvpanels.drawer.waiting')}</Text>
                  ) : (
                    <>
                      {open.device.pairedAt !== undefined && <Text role="caption" tone="secondary" className="tnum">{t('tvpanels.drawer.pairedAt', { when: full(open.device.pairedAt) })}</Text>}
                      <Text role="caption" tone="secondary" className="tnum">{open.lastSeenAt !== undefined ? t('tvpanels.drawer.lastSeen', { when: full(open.lastSeenAt) }) : t('tvpanels.drawer.neverSeen')}</Text>
                    </>
                  )}
                  <Text role="caption" tone="secondary" className="tnum">{t('tvpanels.drawer.createdBy', { name: tvOperatorName(operators, open.createdBy), when: full(open.createdAt) })}</Text>
                  {open.revokedAt !== undefined && (
                    <Text role="caption" tone="secondary" className="tnum">
                      {t('tvpanels.drawer.revokedAt', { when: full(open.revokedAt) })}{open.revokeReason !== undefined ? ` · ${v('tv.revoke_reason', open.revokeReason)}` : ''}
                    </Text>
                  )}
                </Stack>

                {revoking && open.state === 'ACTIVE' && (
                  <Stack gap={3}>
                    <SectionTitle>{t('tvpanels.revoke.title', { name: open.name })}</SectionTitle>
                    <Notice tone="danger">{t('tvpanels.revoke.warn')}</Notice>
                    <Field label={t('tvpanels.revoke.reason')} htmlFor="tvp-revoke-reason">
                      <Select id="tvp-revoke-reason" value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)}>
                        <option value="">{t('tvpanels.revoke.choose')}</option>
                        {TV_REVOKE_REASON_CHOICES.map((r) => (
                          <option key={r} value={r}>{v('tv.revoke_reason', r)}</option>
                        ))}
                      </Select>
                    </Field>
                  </Stack>
                )}

                <Stack gap={3}>
                  <SectionTitle>{t('tvpanels.form.editTitle')}</SectionTitle>
                  <PanelForm
                    idPrefix="tvp-edit"
                    draft={edit}
                    onChange={(d) => setEdit(d)}
                    choices={tvScopeChoices(scope.allowed, open.scope)}
                    scopeOption={scopeOption}
                    options={options}
                    disabled={busy || !mayChangeTvPanel(open, authority.mayManage)}
                  />
                </Stack>

                <Stack gap={2}>
                  <SectionTitle>{t('tvpanels.history.title')}</SectionTitle>
                  <HistoryTimeline
                    entries={historyEntries()}
                    error={eventsError ? t('tvpanels.history.failed') : undefined}
                    onRetry={() => void readEvents(open.panelId)}
                    emptyTitle={t('tvpanels.history.empty')}
                  />
                </Stack>
                {mayDeleteTvPanel(open, authority.mayManage) && !revoking && (
                  <Stack gap={2}>
                    <SectionTitle>{t('tvpanels.delete.title')}</SectionTitle>
                    <Text role="caption" tone="secondary" as="p">{t('tvpanels.delete.caption')}</Text>
                    <Row gap={3}>
                      <Button variant="danger" disabled={busy} onClick={() => void remove(open)}>{t('tvpanels.delete.button')}</Button>
                    </Row>
                  </Stack>
                )}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              {revoking && open.state === 'ACTIVE' ? (
                <>
                  <Button variant="ghost" disabled={busy} onClick={() => { setRevoking(false); setRevokeReason(''); }}>{t('ui.cancel')}</Button>
                  <Button variant="danger" disabled={busy || revokeReason === ''} onClick={() => void revoke(open)}>{t('tvpanels.revoke.confirm')}</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={closeDrawer}>{t('tvpanels.drawer.close')}</Button>
                  {mayChangeTvPanel(open, authority.mayManage) && (
                    <>
                      <Button variant="ghost" disabled={busy} onClick={() => { setRevoking(true); setRevokeReason(''); setDrawerNotice(undefined); }}>{t('tvpanels.drawer.revoke')}</Button>
                      <Button variant="secondary" disabled={busy} onClick={() => void pairAgain(open)}>{t(tvDevicePaired(open) ? 'tvpanels.drawer.repair' : 'tvpanels.drawer.code')}</Button>
                      <Button variant="primary" disabled={busy || !tvDraftValid(edit) || !tvDraftChanged(open, edit)} onClick={() => void save(open)}>{t('tvpanels.form.save')}</Button>
                    </>
                  )}
                </>
              )}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </Stack>
  );
}

// ---- the form (shared by "Novo painel" and the drawer) --------------------------------------------------------------------------------------

function PanelForm({
  idPrefix,
  draft,
  onChange,
  choices,
  scopeOption,
  options,
  disabled,
}: {
  readonly idPrefix: string;
  readonly draft: TvPanelDraft;
  readonly onChange: (draft: TvPanelDraft) => void;
  readonly choices: readonly TvScopeView[];
  readonly scopeOption: (scope: TvScopeView) => string;
  readonly options: readonly number[];
  readonly disabled: boolean;
}): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const viewsId = useId();
  const scope = tvScopeFromValue(draft.scope);
  const rotatable = tvMayRotate(draft.views);
  const citiesOff = scope?.kind === 'MARKET';
  const seconds = options.includes(draft.rotateSeconds) || options.length === 0 ? options : [draft.rotateSeconds, ...options];

  return (
    <Stack gap={4}>
      <Field label={t('tvpanels.form.name')} htmlFor={`${idPrefix}-name`} hint={t('tvpanels.form.nameHint')}>
        <TextInput id={`${idPrefix}-name`} value={draft.name} maxLength={TV_NAME_MAX} disabled={disabled} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
      </Field>

      <Stack gap={2}>
        <Text as="span" role="label" id={viewsId}>{t('tvpanels.form.views')}</Text>
        <Row gap={2} wrap role="group" aria-labelledby={viewsId}>
          {TV_VIEW_CHOICES.map((code: TvViewCode) => {
            const index = draft.views.indexOf(code);
            const allowed = tvViewAllowedInScope(code, scope);
            return (
              <button
                key={code}
                type="button"
                className="vello-chip"
                aria-pressed={index >= 0}
                disabled={disabled || !allowed}
                {...(!allowed ? { 'aria-describedby': `${idPrefix}-cities-off` } : {})}
                onClick={() => onChange(withTvViewToggled(draft, code))}
              >
                {index >= 0 && draft.views.length > 1 && <span className="tnum">{index + 1} · </span>}
                {v('tv.view', code)}
              </button>
            );
          })}
        </Row>
        <Text role="caption" tone="secondary" as="p">{t('tvpanels.form.viewsHint')}</Text>
        {citiesOff && <Text role="caption" tone="secondary" as="p" id={`${idPrefix}-cities-off`}>{t('tvpanels.form.citiesOff')}</Text>}
      </Stack>

      <Field label={t('tvpanels.form.scope')} htmlFor={`${idPrefix}-scope`} hint={t('tvpanels.form.scopeHint')}>
        <Select id={`${idPrefix}-scope`} value={draft.scope} disabled={disabled} onChange={(e) => onChange(withTvScope(draft, e.target.value))}>
          {choices.map((c) => (
            <option key={tvScopeValue(c)} value={tvScopeValue(c)}>{scopeOption(c)}</option>
          ))}
        </Select>
      </Field>

      <Stack gap={2}>
        <Switch
          checked={draft.rotate && rotatable}
          disabled={disabled || !rotatable}
          onChange={(rotate) => onChange({ ...draft, rotate })}
          label={t('tvpanels.form.rotate')}
          aria-describedby={`${idPrefix}-rotate-hint`}
        />
        <Text role="caption" tone="secondary" as="p" id={`${idPrefix}-rotate-hint`}>
          {!rotatable ? t('tvpanels.form.rotateNeedTwo') : draft.rotate ? t('tvpanels.form.rotateOn', { seconds: draft.rotateSeconds }) : t('tvpanels.form.rotateOff')}
        </Text>
        {rotatable && draft.rotate && (
          <Field label={t('tvpanels.form.interval')} htmlFor={`${idPrefix}-interval`}>
            <Select id={`${idPrefix}-interval`} value={String(draft.rotateSeconds)} disabled={disabled} onChange={(e) => onChange({ ...draft, rotateSeconds: Number(e.target.value) })}>
              {seconds.map((s) => (
                <option key={s} value={String(s)}>{t('tvpanels.form.seconds', { seconds: s })}</option>
              ))}
            </Select>
          </Field>
        )}
      </Stack>
    </Stack>
  );
}

// ---- the pairing code, shown once ----------------------------------------------------------------------------------------------------------

function PairingBox({ issued, host, expiresText, onDone }: { readonly issued: Issued; readonly host: string; readonly expiresText: string; readonly onDone: () => void }): ReactElement {
  const { t } = useT();
  return (
    <Stack gap={3}>
      <SectionTitle>{t('tvpanels.pair.title', { name: issued.name })}</SectionTitle>
      <Text role="caption" tone="secondary">{t('tvpanels.pair.code')}</Text>
      <Text role="display" mono as="p" aria-live="polite" className="tnum">{issued.code}</Text>
      <Text as="p">{t('tvpanels.pair.how', { host })}</Text>
      <Notice tone="neutral">{t('tvpanels.pair.expires', { when: expiresText })}</Notice>
      <Text role="caption" tone="secondary" as="p">{t('tvpanels.form.note')}</Text>
      <Row gap={3}>
        <Button variant="secondary" onClick={onDone}>{t('tvpanels.pair.done')}</Button>
      </Row>
    </Stack>
  );
}
